import { useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../../components/ui/accordion";
import { AuditEventList } from "../../../../../components/audit/auditLog/AuditEventList";
import { useAudit } from "../../../../../contexts/auditContext";
import { useAuth } from "../../../../../contexts/authContext";
import { useSessionAuditEvents } from "../../../../../hooks/audit/useAuditEvents";
import { hasPermission } from "../../../../../lib/permissions";

interface AuditLogSectionProps {
  attributeTestColumnId: string;
  sourceRowId?: string;
}

/**
 * Cell-scoped Audit Log accordion for the Attribute Test Result sheet —
 * mirrors `ActivityLogSection`'s shape but sources from the structured audit
 * event feed (`useSessionAuditEvents`) filtered to this column/row. Per the
 * backend's `(column matches OR event has no column) AND (row matches OR
 * event has no row)` semantics, session-level events (reruns, locks, etc.)
 * also surface here alongside cell-specific ones — that's intentional, not
 * a client-side bug to filter out.
 *
 * Fetches only once expanded: the accordion's open state drives the hook's
 * `enabled` flag, so collapsed cells never issue a request.
 */
export function AuditLogSection({
  attributeTestColumnId,
  sourceRowId,
}: AuditLogSectionProps) {
  const { controlSession } = useAudit();
  const { workspaceMember } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const { events, loading, hasMore, loadMore } = useSessionAuditEvents(
    controlSession?.id ?? "",
    { attributeTestColumnId, sourceRowId },
    { enabled: expanded },
  );

  if (!controlSession || !hasPermission(workspaceMember, "view_audit_logs")) {
    return null;
  }

  return (
    <div className="mt-4">
      <Accordion
        type="single"
        collapsible
        value={expanded ? "audit-log" : ""}
        onValueChange={(value) => setExpanded(value === "audit-log")}
      >
        <AccordionItem value="audit-log" className="border-0">
          <AccordionTrigger className="text-sm font-semibold">
            Audit Log
          </AccordionTrigger>
          <AccordionContent>
            <AuditEventList
              events={events}
              loading={loading}
              hasMore={hasMore}
              loadMore={loadMore}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
